<script lang="ts" setup>
const props = defineProps({
  filter: {
    type: Object,
    required: true,
  },
})
const { filter } = toRefs(props)
const emit = defineEmits(['update', 'filter-reset', 'filter-apply'])

const minValue = ref(filter.value.minToBeSelected)
const maxValue = ref(filter.value.maxToBeSelected)

const absoluteMin = computed(() => Math.floor(parseFloat(filter.value.min)))
const absoluteMax = computed(() => Math.ceil(parseFloat(filter.value.max)))

const isPriceFilter = computed(() => filter?.value.key?.includes('price'))

const rangeLabels = computed(() => {
  return {
    from: isPriceFilter.value ? '€' : 'cm',
    to: isPriceFilter.value ? '€' : 'cm',
  }
})

watchEffect (() => {
  minValue.value = filter.value.minToBeSelected
  maxValue.value = filter.value.maxToBeSelected
})

// Only emit when the current values differ from the filter's pending values.
// This prevents the debounced watchers from firing an `update` (and thus a
// navigation) when minValue/maxValue are merely re-synced from new filter data
// after a route change, which otherwise causes a mobile navigation flip-flop.
function isUserAdjustedRange() {
  return Number(minValue.value) !== Number(filter.value.minToBeSelected)
    || Number(maxValue.value) !== Number(filter.value.maxToBeSelected)
}

watchDebounced(
  minValue,
  () => {
    if (!isUserAdjustedRange()) {
      return
    }
    emit('update', { min: minValue.value, max: maxValue.value })
  },
  {
    debounce: 500,
  },
)

watchDebounced(
  maxValue,
  () => {
    if (!isUserAdjustedRange()) {
      return
    }
    emit('update', { min: minValue.value, max: maxValue.value })
  },
  {
    debounce: 500,
  },
)
</script>

<template>
  <div class="filter-range">
    <div class="filter-range__panel">
      <div class="filter-range__item filter-range__min">
        <label
          :for="filter.key + '_min'"
          class="filter-range__label"
        >
          {{ rangeLabels.from }}
        </label>
        <input
          :id="filter.key + '_min'"
          v-model="minValue"
          data-model="minValue"
          type="number"
          :min="absoluteMin"
          :max="absoluteMax"
          class="filter-range__input"
        >
      </div>

      <div>{{ $t('to') }}</div>

      <div class="filter-range__item filter-range__max">
        <label
          :for="filter.key + '_max'"
          class="filter-range__label"
        >
          {{ rangeLabels.to }}
        </label>
        <input
          :id="filter.key + '_max'"
          v-model="maxValue"
          data-model="maxValue"
          type="number"
          :min="absoluteMin"
          :max="absoluteMax"
          class="filter-range__input"
        >
      </div>
    </div>

    <InputMinMaxRange
      v-model:min-value="minValue"
      v-model:max-value="maxValue"
      :min="absoluteMin"
      :max="absoluteMax"
    />

    <div class="filter-range__buttons">
      <button
        type="button"
        class="filter-range__apply"
        @click="$emit('filter-apply')"
      >
        {{ $t('Apply') }}
      </button>

      <button
        type="button"
        class="filter-range__reset"
        @click="$emit('filter-reset')"
      >
        {{ $t('Reset') }}
      </button>
    </div>

    <!-- <pre>filter: {{ filter }}</pre> -->
  </div>
</template>

<style>
@import url("~layers/components/Filter/Range.css");
</style>
